import type { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import type { DefaultError } from '@tanstack/react-query'
import type { JSX } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export interface ValidatedToolMoreDropdownMenuProps {
  validatedTool: ValidatedTool
  response: ValidatedToolResponse
  /**
   * Called ONLY AFTER the response is successfully patched to
   * `entered-in-error`. When provided (the in-note editor context), the caller
   * clears the embedded widget's `response` so it returns to a pristine,
   * re-fillable state instead of staying locked behind a deleted submission.
   * When omitted (the standalone routed response page), a successful delete
   * falls back to navigating back off the page. Either way the unlock/navigate
   * fires on success, not eagerly — so a failed PATCH leaves the widget locked
   * over its still-active QuestionnaireResponse rather than unlocked for a
   * duplicate submission.
   */
  onDelete?: () => void
}

export function ValidatedToolMoreDropdownMenu({
  validatedTool,
  response,
  onDelete,
}: ValidatedToolMoreDropdownMenuProps): JSX.Element {
  const navigate = useNavigate()
  const mutate = usePatchMutation<ValidatedToolResponse>().mutateAsync
  return (
    <MoreDropdownMenu>
      <DropdownMenuDeleteItem
        deleteConfirmationProps={{
          title: `Delete "${validatedTool.name}"?`,
          description:
            'Deleted validated tools can still be recovered by contacting our team.',
          onConfirm: () => {
            mutate({
              instance: response,
              mutateFn: (t) => {
                t.status = 'entered-in-error'
              },
            })
              .then(() => {
                toast.success(`${validatedTool.name} deleted`)
                // Only now that the soft-delete landed: unlock the in-note
                // widget (clears its `response`) or navigate off the standalone
                // page. Doing this before the PATCH resolved risked unlocking
                // the widget over a still-active QR on a failed delete.
                if (onDelete) {
                  onDelete()
                } else {
                  void navigate(-1)
                }
              })
              .catch((error: DefaultError) => {
                toast.error(`Failed to delete ${validatedTool.name}: ${error}`)
              })
          },
        }}
      />
      <DropdownMenuMedplumLinkItem to={response} />
    </MoreDropdownMenu>
  )
}
